import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Upload, Search, TrendingUp, TrendingDown, AlertTriangle, RefreshCw, Mail, X, Filter } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import * as XLSX from 'xlsx';

interface SMAResult {
  ticker: string;
  currentPrice: number;
  sma2: number;
  sma12: number;
  sma50: number;
  sma200: number;
  signal2_12: 'BUY' | 'SELL' | 'NEUTRAL';
  signal50_200: 'BUY' | 'SELL' | 'NEUTRAL';
  crossover2_12: boolean;
  crossover50_200: boolean;
  priceHistory: { date: string; close: number }[];
  error?: string;
}

interface ScanSummary {
  total: number;
  buySignals: number;
  sellSignals: number;
  crossovers: number;
  errors: number;
}

const SMASurveillance = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tickers, setTickers] = useState<string[]>([]);
  const [results, setResults] = useState<SMAResult[]>([]);
  const [summary, setSummary] = useState<ScanSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedStock, setSelectedStock] = useState<SMAResult | null>(null);
  const [showCrossoversOnly, setShowCrossoversOnly] = useState(false);

  const filteredResults = useMemo(() => {
    if (!showCrossoversOnly) return results;
    return results.filter(r => r.crossover2_12 || r.crossover50_200);
  }, [results, showCrossoversOnly]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Get column A values
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        const extractedTickers: string[] = [];
        
        for (let row = range.s.r; row <= range.e.r; row++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 }); // Column A
          const cell = worksheet[cellAddress];
          if (cell && cell.v && typeof cell.v === 'string') {
            const ticker = cell.v.trim().toUpperCase();
            if (ticker && ticker.length > 0 && ticker.length <= 10) {
              extractedTickers.push(ticker);
            }
          }
        }

        setTickers(extractedTickers);
        setResults([]);
        setSummary(null);
        setSelectedStock(null);
        
        toast({
          title: "File Loaded",
          description: `Found ${extractedTickers.length} tickers in column A`,
        });
      } catch (error) {
        toast({
          title: "Error Reading File",
          description: "Could not parse the Excel file",
          variant: "destructive",
        });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleScan = async () => {
    if (tickers.length === 0) {
      toast({
        title: "No Tickers",
        description: "Please upload an Excel file with tickers first",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setProgress(10);
    setResults([]);
    setSummary(null);
    setSelectedStock(null);

    try {
      setProgress(30);
      
      const { data, error } = await supabase.functions.invoke('sma-surveillance', {
        body: { tickers }
      });

      setProgress(90);

      if (error) throw error;

      setResults(data.results || []);
      setSummary(data.summary || null);
      
      toast({
        title: "Scan Complete",
        description: `Analyzed ${data.results?.length || 0} stocks. Email sent to jbmeyerposition@gmail.com`,
      });
    } catch (error) {
      console.error('Scan error:', error);
      toast({
        title: "Scan Failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setProgress(100);
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 500);
    }
  };

  const getSignalBadge = (signal: 'BUY' | 'SELL' | 'NEUTRAL', hasCrossover: boolean) => {
    if (signal === 'BUY') {
      return (
        <Badge className="bg-green-500 hover:bg-green-600">
          <TrendingUp className="w-3 h-3 mr-1" />
          BUY {hasCrossover && '🔄'}
        </Badge>
      );
    }
    if (signal === 'SELL') {
      return (
        <Badge className="bg-red-500 hover:bg-red-600">
          <TrendingDown className="w-3 h-3 mr-1" />
          SELL {hasCrossover && '🔄'}
        </Badge>
      );
    }
    return <Badge variant="secondary">NEUTRAL</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      {loading && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground min-w-[180px]">
                Scanning stocks...
              </span>
              <Progress value={progress} className="flex-1" />
              <span className="text-sm font-semibold text-primary min-w-[50px] text-right">
                {progress}%
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <Search className="h-8 w-8 text-primary" />
              IBKR SMA Surveillance
            </h1>
            <p className="text-muted-foreground">
              Detect SMA 2/12 and 50/200 crossovers in the last 2 trading sessions
            </p>
          </div>
        </div>

        {/* Upload & Scan Controls */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Import Stocks</CardTitle>
            <CardDescription>
              Upload an Excel file with stock tickers in column A
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 items-center">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors">
                  <Upload className="h-4 w-4" />
                  Upload Excel File
                </div>
              </label>

              {tickers.length > 0 && (
                <Badge variant="outline" className="text-sm">
                  {tickers.length} tickers loaded
                </Badge>
              )}

              <Button
                onClick={handleScan}
                disabled={loading || tickers.length === 0}
                className="ml-auto"
              >
                {loading ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Search className="mr-2 h-4 w-4" />
                )}
                Scan for Crossovers
              </Button>
            </div>

            {tickers.length > 0 && (
              <div className="mt-4 p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground mb-2">Loaded tickers:</p>
                <div className="flex flex-wrap gap-1">
                  {tickers.slice(0, 20).map((ticker, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {ticker}
                    </Badge>
                  ))}
                  {tickers.length > 20 && (
                    <Badge variant="secondary" className="text-xs">
                      +{tickers.length - 20} more
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-foreground">{summary.total}</div>
                <div className="text-sm text-muted-foreground">Total Scanned</div>
              </CardContent>
            </Card>
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-green-600">{summary.buySignals}</div>
                <div className="text-sm text-green-600">Buy Signals</div>
              </CardContent>
            </Card>
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-red-600">{summary.sellSignals}</div>
                <div className="text-sm text-red-600">Sell Signals</div>
              </CardContent>
            </Card>
            <Card className="bg-blue-500/10 border-blue-500/30">
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-blue-600">{summary.crossovers}</div>
                <div className="text-sm text-blue-600">Recent Crossovers</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-muted-foreground">{summary.errors}</div>
                <div className="text-sm text-muted-foreground">Errors</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Email Notice */}
        {results.length > 0 && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-primary/10 rounded-md">
            <Mail className="h-4 w-4 text-primary" />
            <span className="text-sm text-foreground">
              Report sent to <strong>jbmeyerposition@gmail.com</strong>
            </span>
          </div>
        )}

        {/* Results Table */}
        {results.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Scan Results</CardTitle>
                  <CardDescription>
                    Click on a row to view the price chart. 🔄 indicates a crossover in the last 2 sessions.
                  </CardDescription>
                </div>
                <Button
                  variant={showCrossoversOnly ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowCrossoversOnly(!showCrossoversOnly)}
                  className="gap-2"
                >
                  <Filter className="h-4 w-4" />
                  {showCrossoversOnly ? 'Showing Crossovers' : 'Show Crossovers Only'}
                  {showCrossoversOnly && (
                    <Badge variant="secondary" className="ml-1">
                      {filteredResults.length}
                    </Badge>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {filteredResults.length === 0 && showCrossoversOnly ? (
                <div className="text-center py-8 text-muted-foreground">
                  No recent crossovers detected in the scanned stocks.
                </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticker</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">SMA 2</TableHead>
                    <TableHead className="text-right">SMA 12</TableHead>
                    <TableHead>Signal 2/12</TableHead>
                    <TableHead className="text-right">SMA 50</TableHead>
                    <TableHead className="text-right">SMA 200</TableHead>
                    <TableHead>Signal 50/200</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredResults.map((result, index) => (
                    <TableRow
                      key={index}
                      className={`cursor-pointer hover:bg-muted/50 ${selectedStock?.ticker === result.ticker ? 'bg-muted' : ''}`}
                      onClick={() => setSelectedStock(result)}
                    >
                      <TableCell className="font-semibold">{result.ticker}</TableCell>
                      <TableCell className="text-right">${result.currentPrice.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{result.sma2.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{result.sma12.toFixed(2)}</TableCell>
                      <TableCell>{getSignalBadge(result.signal2_12, result.crossover2_12)}</TableCell>
                      <TableCell className="text-right">{result.sma50.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{result.sma200 > 0 ? result.sma200.toFixed(2) : 'N/A'}</TableCell>
                      <TableCell>{getSignalBadge(result.signal50_200, result.crossover50_200)}</TableCell>
                      <TableCell>
                        {result.error ? (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Error
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">OK</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Chart Modal for Selected Stock */}
        <Dialog open={!!selectedStock} onOpenChange={(open) => !open && setSelectedStock(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            {selectedStock && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3 text-2xl">
                    <span className="font-bold">{selectedStock.ticker}</span>
                    <Badge variant="outline" className="text-lg px-3 py-1">
                      ${selectedStock.currentPrice.toFixed(2)}
                    </Badge>
                    {selectedStock.signal2_12 === 'BUY' && (
                      <Badge className="bg-green-500">
                        <TrendingUp className="w-4 h-4 mr-1" /> BUY Signal
                      </Badge>
                    )}
                    {selectedStock.signal2_12 === 'SELL' && (
                      <Badge className="bg-red-500">
                        <TrendingDown className="w-4 h-4 mr-1" /> SELL Signal
                      </Badge>
                    )}
                  </DialogTitle>
                  <DialogDescription>
                    90-day price history with all Simple Moving Averages
                  </DialogDescription>
                </DialogHeader>

                {/* SMA Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4">
                  <Card className="bg-purple-500/10 border-purple-500/30">
                    <CardContent className="p-3 text-center">
                      <div className="text-lg font-bold text-purple-600">${selectedStock.sma2.toFixed(2)}</div>
                      <div className="text-xs text-purple-600">SMA 2</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-500/10 border-amber-500/30">
                    <CardContent className="p-3 text-center">
                      <div className="text-lg font-bold text-amber-600">${selectedStock.sma12.toFixed(2)}</div>
                      <div className="text-xs text-amber-600">SMA 12</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-500/10 border-blue-500/30">
                    <CardContent className="p-3 text-center">
                      <div className="text-lg font-bold text-blue-600">${selectedStock.sma50.toFixed(2)}</div>
                      <div className="text-xs text-blue-600">SMA 50</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-500/10 border-red-500/30">
                    <CardContent className="p-3 text-center">
                      <div className="text-lg font-bold text-red-600">
                        {selectedStock.sma200 > 0 ? `$${selectedStock.sma200.toFixed(2)}` : 'N/A'}
                      </div>
                      <div className="text-xs text-red-600">SMA 200</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Main Chart */}
                <StockChart result={selectedStock} />

                {/* Signals Summary */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-muted-foreground">SMA 2/12 Signal</div>
                          <div className="text-lg font-semibold">
                            {selectedStock.signal2_12 === 'BUY' ? 'Short-term Bullish' : 
                             selectedStock.signal2_12 === 'SELL' ? 'Short-term Bearish' : 'Neutral'}
                          </div>
                        </div>
                        {selectedStock.crossover2_12 && (
                          <Badge variant="secondary" className="bg-blue-500/20 text-blue-600">
                            Recent Crossover
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-muted-foreground">SMA 50/200 Signal</div>
                          <div className="text-lg font-semibold">
                            {selectedStock.signal50_200 === 'BUY' ? 'Long-term Bullish' : 
                             selectedStock.signal50_200 === 'SELL' ? 'Long-term Bearish' : 'Neutral'}
                          </div>
                        </div>
                        {selectedStock.crossover50_200 && (
                          <Badge variant="secondary" className="bg-blue-500/20 text-blue-600">
                            Recent Crossover
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Helper function to calculate SMA at each data point
const calculateSMAData = (priceHistory: { date: string; close: number }[], periods: number[]) => {
  const reversed = [...priceHistory].reverse();
  
  return reversed.map((point, index) => {
    const result: { date: string; close: number; sma2?: number; sma12?: number; sma50?: number; sma200?: number } = {
      date: point.date,
      close: point.close,
    };
    
    periods.forEach(period => {
      if (index >= period - 1) {
        const slice = reversed.slice(index - period + 1, index + 1);
        const sum = slice.reduce((acc, p) => acc + p.close, 0);
        const smaKey = `sma${period}` as 'sma2' | 'sma12' | 'sma50' | 'sma200';
        result[smaKey] = sum / period;
      }
    });
    
    return result;
  });
};

// Separate chart component for better organization
const StockChart = ({ result }: { result: SMAResult }) => {
  const chartData = useMemo(() => {
    return calculateSMAData(result.priceHistory, [2, 12, 50, 200]);
  }, [result.priceHistory]);

  return (
    <div className="h-[400px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis
            dataKey="date"
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
            tick={{ fontSize: 11 }}
            interval="preserveStartEnd"
          />
          <YAxis
            domain={['auto', 'auto']}
            tick={{ fontSize: 11 }}
            tickFormatter={(value) => `$${value.toFixed(0)}`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '8px',
            }}
            formatter={(value: number, name: string) => {
              const labels: Record<string, string> = {
                close: 'Price',
                sma2: 'SMA 2',
                sma12: 'SMA 12',
                sma50: 'SMA 50',
                sma200: 'SMA 200',
              };
              return [`$${value?.toFixed(2) || 'N/A'}`, labels[name] || name];
            }}
            labelFormatter={(label) => new Date(label).toLocaleDateString()}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="close"
            name="Price"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="sma2"
            name="SMA 2"
            stroke="#a855f7"
            strokeWidth={1.5}
            dot={false}
            strokeDasharray="2 2"
          />
          <Line
            type="monotone"
            dataKey="sma12"
            name="SMA 12"
            stroke="#f59e0b"
            strokeWidth={1.5}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="sma50"
            name="SMA 50"
            stroke="#3b82f6"
            strokeWidth={1.5}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="sma200"
            name="SMA 200"
            stroke="#ef4444"
            strokeWidth={1.5}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SMASurveillance;
